/** Bet adjustment controls with gold image +/- buttons, bet display, and max bet */

import { Container, Sprite, Texture, Assets, Graphics, Text, TextStyle } from 'pixi.js';
import { GameConfig } from '../config/GameConfig';
import { AudioManager } from '../audio/AudioManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('BetSelector');

const BET_ASSETS = {
  plusNormal: 'assets/ui/plus_button_normal.png',
  plusPressed: 'assets/ui/plus_button_pressed.png',
  plusDisabled: 'assets/ui/plus_button_disabled.png',
  minusNormal: 'assets/ui/minus_button_normal.png',
  minusPressed: 'assets/ui/minus_button_pressed.png',
  minusDisabled: 'assets/ui/minus_button_disabled.png',
};

/** Bet up/down arrows and max bet button */
export class BetSelector extends Container {
  private _enabled: boolean = true;
  private _onBetChange: ((direction: 'up' | 'down' | 'max') => void) | null = null;
  private _currentBetIndex: number = 2; // default 0.05

  // UI elements
  private _betLabel: Text;
  private _betValueText: Text;
  private _totalBetText: Text;
  private _upBtn: Container;
  private _downBtn: Container;
  private _maxBetBtn: Container;

  // Sprite buttons
  private _plusSprite: Sprite | null = null;
  private _minusSprite: Sprite | null = null;
  private _texPlusNormal: Texture | null = null;
  private _texPlusPressed: Texture | null = null;
  private _texPlusDisabled: Texture | null = null;
  private _texMinusNormal: Texture | null = null;
  private _texMinusPressed: Texture | null = null;
  private _texMinusDisabled: Texture | null = null;

  constructor() {
    super();

    const textStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 18,
      fill: 0xcccccc,
    });
    const valueStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial',
      fontSize: 24,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 2 },
    });

    // Bet label
    this._betLabel = new Text({ text: 'BET', style: textStyle });
    this._betLabel.anchor.set(0.5, 1);
    this._betLabel.x = 0;
    this._betLabel.y = -40;
    this.addChild(this._betLabel);

    // Bet value (e.g. "0.05")
    this._betValueText = new Text({
      text: `${GameConfig.BET_LEVELS[this._currentBetIndex].toFixed(2)}`,
      style: valueStyle,
    });
    this._betValueText.anchor.set(0.5);
    this._betValueText.x = 0;
    this._betValueText.y = -15;
    this.addChild(this._betValueText);

    // Total bet display
    this._totalBetText = new Text({
      text: `TOTAL: $${(GameConfig.BET_LEVELS[this._currentBetIndex] * GameConfig.WAGER_MULTIPLIER).toFixed(2)}`,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 14,
        fill: 0xaaaaaa,
      }),
    });
    this._totalBetText.anchor.set(0.5, 0);
    this._totalBetText.x = 0;
    this._totalBetText.y = 2;
    this.addChild(this._totalBetText);

    // Down button (left side) — fallback graphics first
    this._downBtn = this._createArrowButton('down', -70, -15);
    this.addChild(this._downBtn);

    // Up button (right side)
    this._upBtn = this._createArrowButton('up', 70, -15);
    this.addChild(this._upBtn);

    // Max Bet button (below)
    this._maxBetBtn = this._createMaxBetButton();
    this._maxBetBtn.x = 0;
    this._maxBetBtn.y = 35;
    this.addChild(this._maxBetBtn);

    // Load gold button sprite assets
    this._loadAssets();
  }

  get enabled(): boolean {
    return this._enabled;
  }

  /** Set the callback for bet changes */
  onBetChange(callback: (direction: 'up' | 'down' | 'max') => void): void {
    this._onBetChange = callback;
  }

  /** Enable/disable the bet selector */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this.alpha = enabled ? 1 : 0.4;
    this._upBtn.eventMode = enabled ? 'static' : 'none';
    this._downBtn.eventMode = enabled ? 'static' : 'none';
    this._maxBetBtn.eventMode = enabled ? 'static' : 'none';
    this._upBtn.cursor = enabled ? 'pointer' : 'default';
    this._downBtn.cursor = enabled ? 'pointer' : 'default';
    this._maxBetBtn.cursor = enabled ? 'pointer' : 'default';

    // Update sprite textures for disabled state
    if (this._plusSprite && this._texPlusDisabled && this._texPlusNormal) {
      this._plusSprite.texture = enabled ? this._texPlusNormal : this._texPlusDisabled;
    }
    if (this._minusSprite && this._texMinusDisabled && this._texMinusNormal) {
      this._minusSprite.texture = enabled ? this._texMinusNormal : this._texMinusDisabled;
    }
  }

  /** Update the displayed bet level and total bet */
  updateDisplay(betIndex: number): void {
    this._currentBetIndex = betIndex;
    const level = GameConfig.BET_LEVELS[betIndex];
    const total = level * GameConfig.WAGER_MULTIPLIER;
    this._betValueText.text = level.toFixed(2);
    this._totalBetText.text = `TOTAL: $${total.toFixed(2)}`;
  }

  /** Trigger a bet change (called by button handlers) */
  private _triggerChange(direction: 'up' | 'down' | 'max'): void {
    if (!this._enabled) return;

    const audio = AudioManager.getInstance();
    switch (direction) {
      case 'up':
        audio.playSFX('sfx_bet_up');
        break;
      case 'down':
        audio.playSFX('sfx_bet_down');
        break;
      case 'max':
        audio.playSFX('sfx_bet_max');
        break;
    }

    if (this._onBetChange) {
      this._onBetChange(direction);
    }
  }

  private _createArrowButton(direction: 'up' | 'down', x: number, y: number): Container {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.x = x;
    container.y = y;

    // Fallback gold-bordered arrow buttons
    const bg = new Graphics();
    bg.roundRect(-20, -20, 40, 40, 8);
    bg.fill({ color: 0x1a1a3a, alpha: 0.9 });
    bg.stroke({ color: 0xc8960c, width: 2 });
    container.addChild(bg);

    const label = new Text({
      text: direction === 'up' ? '+' : '-',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 28,
        fill: 0xffffff,
      }),
    });
    label.anchor.set(0.5);
    container.addChild(label);

    container.on('pointerdown', () => {
      this._triggerChange(direction);
      // Show pressed texture
      if (direction === 'up' && this._plusSprite && this._texPlusPressed) {
        this._plusSprite.texture = this._texPlusPressed;
      } else if (direction === 'down' && this._minusSprite && this._texMinusPressed) {
        this._minusSprite.texture = this._texMinusPressed;
      }
    });
    container.on('pointerup', () => {
      if (direction === 'up' && this._plusSprite && this._texPlusNormal) {
        this._plusSprite.texture = this._texPlusNormal;
      } else if (direction === 'down' && this._minusSprite && this._texMinusNormal) {
        this._minusSprite.texture = this._texMinusNormal;
      }
    });
    container.on('pointerupoutside', () => {
      if (direction === 'up' && this._plusSprite && this._texPlusNormal) {
        this._plusSprite.texture = this._texPlusNormal;
      } else if (direction === 'down' && this._minusSprite && this._texMinusNormal) {
        this._minusSprite.texture = this._texMinusNormal;
      }
    });

    return container;
  }

  private _createMaxBetButton(): Container {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';

    const bg = new Graphics();
    bg.roundRect(-45, -14, 90, 28, 6);
    bg.fill({ color: 0x1a1a3a, alpha: 0.9 });
    bg.stroke({ color: 0xc8960c, width: 2 });
    container.addChild(bg);

    const label = new Text({
      text: 'MAX BET',
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 14,
        fill: 0xffdd88,
        fontWeight: 'bold',
      }),
    });
    label.anchor.set(0.5);
    container.addChild(label);

    container.on('pointerdown', () => this._triggerChange('max'));

    return container;
  }

  private async _loadAssets(): Promise<void> {
    try {
      const [plusN, plusP, plusD, minusN, minusP, minusD] = await Promise.all([
        Assets.load<Texture>(BET_ASSETS.plusNormal).catch(() => null),
        Assets.load<Texture>(BET_ASSETS.plusPressed).catch(() => null),
        Assets.load<Texture>(BET_ASSETS.plusDisabled).catch(() => null),
        Assets.load<Texture>(BET_ASSETS.minusNormal).catch(() => null),
        Assets.load<Texture>(BET_ASSETS.minusPressed).catch(() => null),
        Assets.load<Texture>(BET_ASSETS.minusDisabled).catch(() => null),
      ]);

      if (plusN && minusN) {
        this._texPlusNormal = plusN;
        this._texPlusPressed = plusP;
        this._texPlusDisabled = plusD;
        this._texMinusNormal = minusN;
        this._texMinusPressed = minusP;
        this._texMinusDisabled = minusD;

        // Replace fallback + button with sprite
        this._plusSprite = new Sprite(plusN);
        this._plusSprite.anchor.set(0.5);
        // Native 50x65 — keep it as-is, scale from layout
        this._upBtn.removeChildren();
        this._upBtn.addChild(this._plusSprite);

        // Replace fallback - button with sprite
        this._minusSprite = new Sprite(minusN);
        this._minusSprite.anchor.set(0.5);
        this._downBtn.removeChildren();
        this._downBtn.addChild(this._minusSprite);

        // Re-attach pointer events since we cleared children
        // (events are on the container, not the children, so they still work)

        logger.info('Bet +/- button sprites loaded');
      } else {
        logger.debug('Bet button sprite assets not available, using fallback graphics');
      }
    } catch (err) {
      logger.debug(`Bet assets not loaded: ${err}`);
    }
  }
}
